import { useState, useRef } from "react"
import styles from "./avatar-upload.module.css"

export default function AvatarUpload({ onUpload }) {
  const [preview, setPreview] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const fileInputRef = useRef(null)

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        const img = new Image()
        img.src = event.target?.result
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")

          const maxWidth = 512
          const maxHeight = 512
          let width = img.width
          let height = img.height

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width
              width = maxWidth
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height
              height = maxHeight
            }
          }

          canvas.width = width
          canvas.height = height
          ctx?.drawImage(img, 0, 0, width, height)

          resolve(canvas.toDataURL("image/jpeg", 0.8))
        }
      }
    })
  }

  const handleFile = async (file) => {
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file")
      return
    }

    setIsCompressing(true)
    try {
      const compressedUrl = await compressImage(file)
      setPreview(compressedUrl)
      onUpload(compressedUrl)
    } catch (error) {
      console.error("Error compressing image:", error)
      alert("Error processing image")
    } finally {
      setIsCompressing(false)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
  }

  return (
    <div className={styles.uploadContainer}>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dragging : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={styles.fileInput}
        />

        <div className={styles.dropContent}>
          <svg className={styles.uploadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p className={styles.dropText}>{isCompressing ? "Compressing..." : "Drag and drop your image here"}</p>
          <p className={styles.dropSubtext}>or click to select a file</p>
        </div>
      </div>

      {preview && (
        <div className={styles.previewContainer}>
          <h3 className={styles.previewTitle}>Preview</h3>
          <img src={preview || "/placeholder.svg"} alt="Preview" className={styles.previewImage} />
          <p className={styles.previewInfo}>Image compressed and ready to use</p>
        </div>
      )}
    </div>
  )
}
